import { Box, List, ListItemButton, Stack, Typography, useTheme } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import Tools from "@/components/Icons/Tools";
import WorkspaceUserProfile from "@/components/Workspace/WorkspaceUserProfile";
import WorkspaceChannelListContainer from "@/components/WorkspaceChannelList/WorkspaceChannelListContainer";
import { handsFreeEnabledForWorkspace } from "@/data/workspaceConfig";
import UseFirebase from "@/hooks/firebase/useFirebase";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import Locator from "@/locator";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import ChannelDial from "../Icons/ChannelDial";
import ChannelDialOutlined from "../Icons/ChannelDialOutlined";
import WorkspaceDrawerHeader from "./WorkspaceDrawerHeader";
import WorkspaceTools from "./WorkspaceTools";

function WorkspaceList() {
  const { userReadOnlyMode } = useContext(UxContext);
  const { toolsTab, handsFreePlayback } = useGlobalFlags();
  const { isWorkspaceLimitedMember, currentWorkspaceId } = React.useContext(WorkspaceContext);
  const workspaceId = currentWorkspaceId;
  const { myAccount } = React.useContext(MyAccountContext);
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<string>("channels");
  const urlContainsTools = window?.location.pathname.includes("/tools/");
  const navigate = useNavigate();

  const toggleActiveTab = (key: string) => {
    setActiveTab(() => key);
    if (urlContainsTools) {
      const newPath = window.location.pathname
        ?.replace("/tools/template-management", "")
        ?.replace("/tools/scheduled-messages", "");
      navigate(newPath);
    }
  };
  const { ActivateFirebaseBanner, BrowserCantGetTokenModal, showNotificationsBanner } = UseFirebase();
  const showHandsFreeToggle = handsFreePlayback && handsFreeEnabledForWorkspace(workspaceId);
  const limitedMember = isWorkspaceLimitedMember();

  const initialsContainer = (
    <Box>
      <AvatarWithStatus size={24} accountId={myAccount?.id} />
    </Box>
  );

  const WorkspaceTabs = useMemo(
    () =>
      [
        {
          enabled: true,
          showCta: true,
          text: "Channels",
          key: "channels",
          tabKey: null,
          active: false,
          component: <WorkspaceChannelListContainer notificationsBanner={showNotificationsBanner} />,
          background: "transparent",
          icons: {
            active: <ChannelDial />,
            inActive: <ChannelDialOutlined />,
          },
        },
        {
          enabled: toolsTab && !limitedMember,
          showCta: true,
          text: "Tools",
          key: "tools",
          tabKey: "tools",
          active: false,
          component: <WorkspaceTools />,
          background: theme.palette.secondary.dark,
          icons: {
            active: <Tools style={{ fill: theme.palette.primary.main }} />,
            inActive: <Tools style={{ fill: theme.palette.neutral.main }} />,
          },
        },
        {
          enabled: true,
          showCta: true,
          text: "You",
          key: "you",
          tabKey: "you",
          active: false,
          component: <WorkspaceUserProfile />,
          background: theme.palette.secondary.dark,
          icons: { active: initialsContainer, inActive: initialsContainer },
        },
      ]
        .map((item) => ({
          ...item,
          active: activeTab === item.key,
          icon: activeTab === item.key ? item.icons.active : item.icons.inActive,
        }))
        .filter((tab) => tab.enabled)
        .filter((tab) => !(userReadOnlyMode && tab.key === "tools")),
    [
      userReadOnlyMode,
      activeTab,
      showHandsFreeToggle,
      showNotificationsBanner,
      theme.palette.secondary.dark,
      theme.palette.primary.main,
      theme.palette.neutral.main,
    ],
  );

  const currentActiveTab = useMemo(
    () => WorkspaceTabs.find((tab) => tab.key === activeTab),
    [WorkspaceTabs, activeTab],
  );

  return (
    <Stack
      sx={{
        display: "grid",
        gridTemplateRows: "auto minmax(150px, 1fr) auto",
        background: theme.palette.primary.dark,
        gap: 1.5,
        height: "100%",
      }}
    >
      <WorkspaceDrawerHeader />
      <Stack
        sx={{
          gap: 2,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {BrowserCantGetTokenModal()}
        {showNotificationsBanner && ActivateFirebaseBanner()}
        <Box
          sx={{
            height: "100%",
            borderRadius: 4,
            background: currentActiveTab?.background,
          }}
        >
          {currentActiveTab?.component}
        </Box>
      </Stack>
      <List
        sx={{
          display: "flex",
          p: 0,
        }}
      >
        {WorkspaceTabs?.map((tab) => (
          <ListItemButton
            key={tab.key}
            aria-label={`Toggle ${tab.text} Tab`}
            data-testid={Locator.workspaceNav.workspaceTab(tab.key)}
            sx={{
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              color: tab.active ? theme.palette.primary.main : theme.palette.neutral.main,
              gap: 0.5,
              background: tab.active ? theme.palette.secondary.main : "transparent",
              width: 90,
              height: 56,
              borderRadius: "8px",
              p: 1,
            }}
            onClick={() => {
              toggleActiveTab(tab.key);
            }}
          >
            {tab.icon}
            <Typography
              sx={{
                fontSize: "12px",
                textTransform: "none",
                fontWeight: "500",
                lineHeight: "16px",
              }}
            >
              {tab.text}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    </Stack>
  );
}

export default WorkspaceList;
